// By path, so a host compiling this source without the package's tsconfig (campaign-ui) still
// knows `import.meta.env`.
/// <reference path="../env.d.ts" />
import { inject, provide, toValue, type MaybeRefOrGetter } from 'vue'
import {
  track,
  type ConversionSubject,
  type FormOrg,
  type FormPayer,
  type FormType,
} from 'fundraising-core'
import {
  FUNDRAISING_TRACKING_KEY,
  FUNDRAISING_TRACKING_NAME_KEY,
  FUNDRAISING_TRACKING_ORG_KEY,
  type FundraisingTracking,
} from './fundraisingTrackingContext'

export {
  FUNDRAISING_DEFERS_CONVERSION_KEY,
  FUNDRAISING_TRACKING_KEY,
  FUNDRAISING_TRACKING_NAME_KEY,
  FUNDRAISING_TRACKING_ORG_KEY,
  useFundraisingTracking,
  type FundraisingTracking,
} from './fundraisingTrackingContext'

/** For a host: names the form (Matomo event name) for every flow under it, e.g. the page slug. */
export function provideFundraisingTrackingName(name: MaybeRefOrGetter<string | undefined>) {
  provide(FUNDRAISING_TRACKING_NAME_KEY, name)
}

type OrgLang = {
  org?: MaybeRefOrGetter<FormOrg | undefined>
  lang?: MaybeRefOrGetter<string | undefined>
}

/** For a host: the organisation every flow under it collects for, and the form's language. */
export function provideFundraisingTrackingOrg(
  org: MaybeRefOrGetter<FormOrg | undefined>,
  lang?: MaybeRefOrGetter<string | undefined>,
) {
  provide<OrgLang>(FUNDRAISING_TRACKING_ORG_KEY, { org, lang })
}

/**
 * A form's tracking context outside a flow, for a page that drives its own steps. `campaign` is
 * the CRM code the form submits with, after its own defaults.
 */
export function createFundraisingTracking(ctx: {
  type: MaybeRefOrGetter<FormType>
  payer?: FormPayer
  name?: MaybeRefOrGetter<string | undefined>
  campaign?: MaybeRefOrGetter<string | undefined>
  org?: MaybeRefOrGetter<FormOrg | undefined>
  lang?: MaybeRefOrGetter<string | undefined>
}): FundraisingTracking {
  let started = false
  const subject = (): ConversionSubject => ({
    type: toValue(ctx.type),
    payer: ctx.payer ?? 'private',
    name: toValue(ctx.name),
    org: toValue(ctx.org),
    lang: toValue(ctx.lang),
  })
  return {
    subject,
    track(action, value) {
      const base = { ...subject(), campaign: toValue(ctx.campaign) }
      if (!started) {
        started = true
        track({ ...base, action: 'start' })
      }
      track({ ...base, action, value })
    },
  }
}

let warnedNoOrg = false

// Vite replaces only the literal `import.meta.env.DEV` (not a cast or `?.` form); outside Vite
// `import.meta.env` is undefined and this throws.
function isDevBuild(): boolean {
  try {
    return import.meta.env.DEV === true
  } catch {
    return false
  }
}

/** For a flow: its tracking context, provided to the form inside it. */
export function provideFundraisingTracking(
  type: MaybeRefOrGetter<FormType>,
  payer: FormPayer = 'private',
  campaign?: MaybeRefOrGetter<string | undefined>,
  /** In place of the host's form name, for a mode of the flow that isn't that form's funnel. */
  name?: MaybeRefOrGetter<string | undefined>,
): FundraisingTracking {
  const { org, lang } = inject<OrgLang>(FUNDRAISING_TRACKING_ORG_KEY, {})
  // Without an org no conversion ever fires, and nothing else says so.
  if (org === undefined && !warnedNoOrg && isDevBuild()) {
    warnedNoOrg = true
    console.warn(
      '[fundraising-ui] No tracking org: call provideFundraisingTrackingOrg (the ' +
        `\`${FUNDRAISING_TRACKING_ORG_KEY}\` provide) in the host, or Google Ads conversions never fire.`,
    )
  }
  const hostName = inject<MaybeRefOrGetter<string | undefined>>(
    FUNDRAISING_TRACKING_NAME_KEY,
    undefined,
  )
  const tracking = createFundraisingTracking({
    type,
    payer,
    campaign,
    name: () => toValue(name) ?? toValue(hostName),
    org,
    lang,
  })
  provide(FUNDRAISING_TRACKING_KEY, tracking)
  return tracking
}
